import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
    CustomizedApplicationPayload,
    EditPublishedApplicationPayload,
    FileModel,
    ItemsToBeRemovedOptions,
    ListResponse,
    MlModelWithSegmentationSettings,
    ModelConfig,
    NameAndSelectedFilesContinueInformation,
    SearchOptions,
    SupportTypes,
    WorkflowSimple,
    WorkflowType,
} from '@ca/core/models';
import {
    DialogService,
    ExperimentalFeaturesService,
    MachineLearningService,
    SupportService,
    UserNotificationService,
    WorkflowV1Service,
} from '@ca/core/services';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Guid } from 'guid-typescript';
import { BehaviorSubject, EMPTY, Observable, Subject, forkJoin, from, of, zip } from 'rxjs';
import { catchError, finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { MLMeasurement } from 'src/app/core/models/measurement.model';
import { AriApplicationService } from 'src/app/core/services/ari-application.service';
import { CziConverterService } from 'src/app/core/services/czi-converter.service';
import { CreateAndStartDialogComponent } from 'src/app/shared/components/create-and-start-dialog/create-and-start-dialog.component';
import { APPLICATIONS_ROUTE } from 'src/app/shared/constants/constants';
import * as FileUtils from '../../../../../utils/file/file-utils';
import { ApplicationType } from '../../../../core/enums/application-type.enum';
import { ApplicationDetail, ApplicationOverview, PublishCustomizedApplicationPayload } from '../../../../core/models/application.model';
import { ContactUsComponent } from '../../../../shared/components/contact-us/contact-us.component';
import { DeleteConfirmationDialogComponent } from '../../../../shared/components/delete-confirmation-dialog/delete-confirmation-dialog.component';
import { CreateApplicationDialogComponent } from '../../components/create-application-dialog/create-application-dialog.component';
import { EditApplicationDialogComponent } from '../../components/edit-application-dialog/edit-application-dialog.component';
import { PublishApplicationDialogComponent } from '../../components/publish-application-dialog/publish-application-dialog.component';
import { RequestApplicationDialogComponent } from '../../components/request-application-dialog/request-application-dialog.component';
import { ApplicationsService } from '../applications/applications.service';
import { MLModelService } from '../ml-model/ml-model.service';

@UntilDestroy()
@Injectable()
export class ApplicationsFacadeService {
    constructor(
        private applicationsService: ApplicationsService,
        private cziConverterService: CziConverterService,
        private dialogService: DialogService,
        private experimentalFeatures: ExperimentalFeaturesService,
        private machineLearningService: MachineLearningService,
        private mlModelService: MLModelService,
        private modalService: NgbModal,
        private router: Router,
        private supportService: SupportService,
        private translateService: TranslateService,
        private userNotificationService: UserNotificationService,
        private workflowV1Service: WorkflowV1Service,
        private ariApplicationService: AriApplicationService,
    ) {}

    private isLoading$$ = new BehaviorSubject(false);
    private totalCount$$ = new BehaviorSubject(0);
    private applications$$ = new BehaviorSubject<ApplicationOverview[]>([]);
    private applicationDetail$$: BehaviorSubject<ApplicationDetail | null> = new BehaviorSubject(null);
    private refresh$$ = new Subject<void>();
    private createAndStartDialogClosed$$ = new Subject<boolean>();
    isMEDProjectEnabled: boolean = this.experimentalFeatures.isMEDProjectActive;

    get isLoading$(): Observable<boolean> {
        return this.isLoading$$.asObservable();
    }

    get totalCount$(): Observable<number> {
        return this.totalCount$$.asObservable();
    }

    get applications$(): Observable<ApplicationOverview[]> {
        return this.applications$$.asObservable();
    }

    get applicationDetail$(): Observable<ApplicationDetail | null> {
        return this.applicationDetail$$.asObservable();
    }

    get refresh$(): Observable<void> {
        return this.refresh$$.asObservable();
    }

    searchApplications(searchOptions: SearchOptions, type: ApplicationType): void {
        this.isLoading$$.next(true);
        this.applicationsService
            .searchApplications(searchOptions, type)
            .pipe(
                tap((response: ListResponse<ApplicationOverview>): void => {
                    this.totalCount$$.next(response?.meta?.totalCount || 0);
                }),
                tap(response => this.applications$$.next(response.data)),
                catchError((): Observable<never> => {
                    this.userNotificationService.showError('APPLICATIONS.GET_APPLICATIONS_ERROR');
                    return EMPTY;
                }),
                finalize((): void => this.isLoading$$.next(false)),
            )
            .subscribe();
    }

    getApplicationById(id: string): void {
        this.isLoading$$.next(true);
        this.applicationsService
            .getApplicationById$(id)
            .pipe(
                tap(applicationDetail => {
                    this.applicationDetail$$.next(applicationDetail);
                }),
                catchError((): Observable<never> => {
                    this.userNotificationService.showError('APPLICATIONS.GET_APPLICATION_ERROR');
                    return EMPTY;
                }),
                finalize((): void => this.isLoading$$.next(false)),
            )
            .subscribe();
    }

    createApplication(trainingId?: string): void {
        const createApplicationModal = this.modalService.open(CreateApplicationDialogComponent, { size: 'xl', centered: true });
        const createApplicationModalComponent = createApplicationModal.componentInstance as CreateApplicationDialogComponent;
        createApplicationModalComponent.trainingId = trainingId;
        createApplicationModalComponent.onApplicationDataSet
            .pipe(
                tap((): void => {
                    this.userNotificationService.showSpinner();
                }),
                switchMap(
                    (data: {
                        applicationName: string;
                        selectedModel: MlModelWithSegmentationSettings;
                        selectedMeasurements: MLMeasurement[];
                        selectedWorkflow?: WorkflowSimple;
                        closeDialog: Function;
                    }) => {
                        return zip(
                            of(data),
                            this.workflowV1Service.getSuccessorsWorkflowById(data.selectedModel.workflowId, WorkflowType.SEGMENTATION).pipe(
                                map(workflow => workflow.id),
                                catchError((): Observable<never> => {
                                    this.userNotificationService.showError('APPLICATIONS.CREATE_APPLICATION_DIALOG.ERRORS.GET_SEGMENTATION_WORKFLOW');
                                    return EMPTY;
                                }),
                                finalize((): void => this.userNotificationService.hideSpinner()),
                            ),
                        );
                    },
                ),
                tap((): void => {
                    this.userNotificationService.showSpinner();
                }),
                switchMap(zipped => {
                    const [{ applicationName, selectedModel, selectedMeasurements, selectedWorkflow, closeDialog }, segmentationWorkflowId] = zipped;
                    return this.applicationsService
                        .createApplication$({
                            applicationName: applicationName.trim(),
                            trainingId: selectedModel.trainingId,
                            workflowId: segmentationWorkflowId,
                            measurementIds: selectedMeasurements.map(m => m.id),
                            ...(selectedWorkflow && { customWorkflowId: selectedWorkflow.id }),
                        })
                        .pipe(
                            tap((application: ApplicationDetail): void => {
                                closeDialog();
                                const title = this.translateService.instant('APPLICATIONS.CREATE_APPLICATION_DIALOG.APPLICATION_CREATED_TITLE');
                                const message = this.translateService.instant('APPLICATIONS.CREATE_APPLICATION_DIALOG.APPLICATION_CREATED_MESSAGE');
                                const buttonText = this.translateService.instant('APPLICATIONS.CREATE_APPLICATION_DIALOG.APPLICATION_CREATED_OPEN');
                                this.dialogService.showSuccess({
                                    title,
                                    message,
                                    buttonText,
                                    buttonAction: (): void => {
                                        this.openApplication(application.id);
                                    },
                                });
                            }),
                            catchError((err: unknown): Observable<never> => {
                                if (err instanceof HttpErrorResponse) {
                                    this.userNotificationService.showError(err.error as string);
                                }
                                return EMPTY;
                            }),
                            finalize((): void => {
                                this.userNotificationService.hideSpinner();
                                this.refresh$$.next();
                            }),
                        );
                }),
                take(1),
                finalize((): void => this.userNotificationService.hideSpinner()),
            )
            .subscribe();
    }

    deleteApplication(name: string, id: string): void {
        const modalRef = this.modalService.open(DeleteConfirmationDialogComponent, { size: 'md' });
        const componentInstance = modalRef.componentInstance as DeleteConfirmationDialogComponent;
        componentInstance.title = this.translateService.instant('APPLICATIONS.DELETE_APPLICATION');
        componentInstance.description = this.translateService.instant('APPLICATIONS.APPLICATION_NAME', { name });
        componentInstance.inputLabel = this.translateService.instant('APPLICATIONS.ENTER_APPLICATION_NAME_TO_CONFIRM');
        componentInstance.confirmationText = name;
        from(modalRef.result)
            .pipe(
                switchMap((): Observable<void> => {
                    this.userNotificationService.showSpinner();
                    return this.applicationsService.deleteApplication$(id);
                }),
                tap((): void => this.refresh$$.next()),
                finalize((): void => this.userNotificationService.hideSpinner()),
            )
            .subscribe();
    }

    requestApplication(): void {
        const requestApplicationModal = this.modalService.open(RequestApplicationDialogComponent);
        const requestApplicationModalComponent = requestApplicationModal.componentInstance as RequestApplicationDialogComponent;
        requestApplicationModalComponent.onSubmit
            .pipe(
                tap((): void => {
                    this.userNotificationService.showSpinner();
                }),
                switchMap((payload: CustomizedApplicationPayload): Observable<void> => this.supportService.requestApplication$(payload)),
                tap((): void => {
                    this.userNotificationService.hideSpinner();
                    requestApplicationModalComponent.dismiss();
                    const title = this.translateService.instant('APPLICATIONS.REQUEST_CUSTOMIZED_APPLICATION.DIALOG.TITLE');
                    const message = this.translateService.instant('APPLICATIONS.REQUEST_CUSTOMIZED_APPLICATION.DIALOG.MESSAGE');
                    const buttonText = this.translateService.instant('APPLICATIONS.REQUEST_CUSTOMIZED_APPLICATION.DIALOG.BTN_TEXT');
                    this.dialogService.showSuccess({ title, message, buttonText });
                }),
                finalize((): void => {
                    requestApplicationModalComponent.dismiss();
                    this.userNotificationService.hideSpinner();
                }),
            )
            .subscribe();
    }

    publishApplication(applicationId: string): void {
        this.userNotificationService.showSpinner();
        let publishApplicationModalComponent: PublishApplicationDialogComponent;
        this.applicationsService
            .getApplicationById$(applicationId)
            .pipe(
                catchError((): Observable<never> => {
                    this.userNotificationService.showError('APPLICATI: Observable<void: void>ONS.PUBLISH_CUSTOMIZED_APPLICATION.ERRORS.GET_DATA');
                    return EMPTY;
                }),
                tap((): void => {
                    this.userNotificationService.hideSpinner();
                }),
                switchMap(application => {
                    const publishApplicationModal = this.modalService.open(PublishApplicationDialogComponent);
                    publishApplicationModalComponent = publishApplicationModal.componentInstance as PublishApplicationDialogComponent;
                    publishApplicationModalComponent.pathToAppend = applicationId;
                    publishApplicationModalComponent.application = application;
                    return publishApplicationModalComponent.onSubmit;
                }),
                tap((): void => {
                    this.userNotificationService.showSpinner();
                }),
                map((payload: PublishCustomizedApplicationPayload) => {
                    return { ...{ applicationId }, ...payload };
                }),
                switchMap((payload: PublishCustomizedApplicationPayload): Observable<void> => this.applicationsService.publishApplication$(payload)),
                tap((): void => {
                    this.userNotificationService.hideSpinner();
                    publishApplicationModalComponent.dismiss();
                    this.userNotificationService.showSuccess('APPLICATIONS.PUBLISH_CUSTOMIZED_APPLICATION.PUBLISH_SUCCESS');
                    this.refresh$$.next();
                }),
            )
            .subscribe();
    }

    unPublishApplication(applicationId: string, applicationName: string): void {
        const modalTitle = this.translateService.instant('APPLICATIONS.UNPUBLISH_CUSTOMIZED_APPLICATION.DIALOG.TITLE', { applicationName });
        const message = this.translateService.instant('APPLICATIONS.UNPUBLISH_CUSTOMIZED_APPLICATION.DIALOG.MESSAGE');
        const confirmModal = this.dialogService.openConfirm({ modalTitle, message });
        from(confirmModal.result)
            .pipe(
                tap((): void => this.userNotificationService.showSpinner()),
                switchMap((): Observable<void> => this.applicationsService.unPublishApplication$(applicationId)),
                tap((): void => {
                    this.userNotificationService.hideSpinner();
                    this.refresh$$.next();
                }),
            )
            .subscribe();
    }

    editPublishedApplication(applicationId: string): void {
        this.userNotificationService.showSpinner();
        let editApplicationDialogComponent: EditApplicationDialogComponent;
        forkJoin([
            this.applicationsService.getApplicationById$(applicationId),
            this.machineLearningService.fetchAllMachineLearningModels(true),
            this.machineLearningService.getMeasurements$(),
        ])
            .pipe(
                catchError((): Observable<never> => {
                    this.userNotificationService.showError('APPLICATIONS.PUBLISH_CUSTOMIZED_APPLICATION.ERRORS.GET_DATA');
                    return EMPTY;
                }),
                tap((): void => {
                    this.userNotificationService.hideSpinner();
                }),
                switchMap(([application, mlModels, measurements]) => {
                    const editApplicationModal = this.modalService.open(EditApplicationDialogComponent);
                    editApplicationDialogComponent = editApplicationModal.componentInstance as EditApplicationDialogComponent;
                    editApplicationDialogComponent.pathToAppend = applicationId;
                    editApplicationDialogComponent.application = application;
                    editApplicationDialogComponent.models = mlModels;
                    editApplicationDialogComponent.measurements = measurements;
                    editApplicationDialogComponent.requestMeasurement = (): void => this.requestMlMeasurement();
                    return editApplicationDialogComponent.onSubmit;
                }),
                map((payload: EditPublishedApplicationPayload) => {
                    return { ...{ applicationId }, ...payload };
                }),
                switchMap(
                    (payload: EditPublishedApplicationPayload): Observable<void> =>
                        this.applicationsService.editApplication$(payload).pipe(
                            catchError((): Observable<never> => {
                                this.userNotificationService.showError('APPLICATIONS.PUBLISH_CUSTOMIZED_APPLICATION.ERRORS.EDIT');
                                return EMPTY;
                            }),
                        ),
                ),
                tap((): void => {
                    editApplicationDialogComponent.dismiss();
                    this.userNotificationService.showSuccess('APPLICATIONS.PUBLISH_CUSTOMIZED_APPLICATION.PUBLISH_SUCCESS');
                    this.refresh$$.next();
                }),
                finalize((): void => this.userNotificationService.hideSpinner()),
            )
            .subscribe();
    }

    runApplication(applicationName: string, modelId: string, isCziRequired: boolean = true): void {
        const operationId = Guid.create().toString();
        let selectedFiles: FileModel[] = [];
        const modal: NgbModalRef = this.modalService.open(CreateAndStartDialogComponent, {
            size: this.isMEDProjectEnabled ? 'upload-and-select-files' : 'upload-files',
            scrollable: true,
            centered: true,
            backdrop: 'static',
            keyboard: false,
        });
        const createAndStartDialogComponent: CreateAndStartDialogComponent = modal.componentInstance;
        createAndStartDialogComponent.modalTitleTranslationKey = this.isMEDProjectEnabled
            ? 'APPLICATIONS.RUN_APPLICATION.MED_TITLE'
            : 'APPLICATIONS.RUN_APPLICATION.TITLE';
        createAndStartDialogComponent.nameLabelTranslationKey = 'APPLICATIONS.RUN_APPLICATION.NAME_LABEL';
        createAndStartDialogComponent.isSelectedFilesShown = this.isMEDProjectEnabled;
        createAndStartDialogComponent.namePlaceholderTranslationKey = 'APPLICATIONS.RUN_APPLICATION.NAME_PLACEHOLDER';
        createAndStartDialogComponent.continueButtonTranslationKey = 'APPLICATIONS.RUN_APPLICATION.START_BUTTON_TEXT';
        createAndStartDialogComponent.parentFolderName = applicationName;
        createAndStartDialogComponent.operationId = operationId;
        createAndStartDialogComponent.isMEDCZIDisabled = !isCziRequired;
        createAndStartDialogComponent.isMEDProjectEnabled = this.isMEDProjectEnabled;
        createAndStartDialogComponent.columns = this.isMEDProjectEnabled
            ? [
                  {
                      key: 'sizeInBytes',
                      translationKey: 'GENERAL.COLUMN.SIZE',
                      defaultValue: '\u2014',
                      transform: 'fileSize',
                  },
              ]
            : [
                  { key: 'fileStatus' },
                  {
                      key: 'sizeInBytes',
                      translationKey: 'GENERAL.COLUMN.SIZE',
                      defaultValue: '\u2014',
                      transform: 'fileSize',
                  },
              ];
        createAndStartDialogComponent.selectedFiles = [...selectedFiles];
        createAndStartDialogComponent.isUploadOfFilesEnabled = !this.isMEDProjectEnabled;
        createAndStartDialogComponent.supportedFileTypes = isCziRequired
            ? FileUtils.machineLearningSupportedFileTypes
            : FileUtils.ariApplicationsSupportedFileTypes;
        createAndStartDialogComponent.setNewModalSize
            .pipe(
                tap((size: string): void => {
                    // we have to use internal NgbModal API to set the size after the modal was opened
                    modal['_windowCmptRef'].instance.size = size;
                }),
                takeUntil(this.createAndStartDialogClosed$$),
            )
            .subscribe();

        createAndStartDialogComponent.continue
            .pipe(
                tap((): void => {
                    this.userNotificationService.showSpinner();
                }),
                switchMap((applicationRunInformation: NameAndSelectedFilesContinueInformation) => {
                    const inputFiles =
                        applicationRunInformation.files != null ? applicationRunInformation.files.map((file: FileModel): string => file.token || '') : [];
                    const inputImages = inputFiles.map(inputImage => FileUtils.getFileNameWithExtension(inputImage));
                    return this.mlModelService.getConfig$(modelId).pipe(
                        switchMap(modelConfig => {
                            // Conditionally call createSettingsFile$ based on modelConfig
                            if (modelConfig.modelUrl != undefined) {
                                return this.mlModelService.createSettingsFile$(modelId, inputImages).pipe(
                                    switchMap(settingsFileUrl => {
                                        // Proceed with the rest of the process after creating the settings file
                                        return this.proceedWithSegmentationWorkflow$(modelConfig, settingsFileUrl, inputFiles, applicationRunInformation);
                                    }),
                                );
                            } else {
                                // Directly proceed without creating settings file, using null or an appropriate default value for settingsFileUrl
                                return this.proceedWithAriWorkflow$(modelConfig, inputFiles, applicationRunInformation);
                            }
                        }),
                        catchError((err: unknown): Observable<never> => {
                            this.userNotificationService.hideSpinner();
                            return this.handleStartWorkflowError$(err);
                        }),
                    );
                }),
                tap((zipped: [{ workflowRunId: string }, NameAndSelectedFilesContinueInformation]): void => {
                    const applicationRunInformation = zipped[1];
                    applicationRunInformation.closeDialog();
                    this.createAndStartDialogClosed$$.next(true);
                }),
                catchError((err: unknown): Observable<never> => {
                    return this.handleStartWorkflowError$(err);
                }),
                takeUntil(this.createAndStartDialogClosed$$),
                finalize((): void => {
                    this.userNotificationService.hideSpinner();
                }),
            )
            .subscribe();

        createAndStartDialogComponent.selectedFilesUpdatedBecauseOfUpload
            .pipe(
                tap((files: FileModel[]): void => {
                    selectedFiles = files;
                }),
            )
            .subscribe();

        createAndStartDialogComponent.addFiles
            .pipe(
                tap((filesToAdd: FileModel[]): void => {
                    selectedFiles = [...filesToAdd, ...selectedFiles];
                    filesToAdd.forEach((file: FileModel): void => {
                        this.cziConverterService
                            .convertFileToCziIfNotCziAndStartPolling$(
                                operationId,
                                [...filesToAdd, ...selectedFiles],
                                file,
                                undefined,
                                undefined,
                                !isCziRequired,
                            )
                            .pipe(
                                tap((updatedFiles: FileModel[]): void => {
                                    selectedFiles = [...FileUtils.getUpdatedSelectedFiles(selectedFiles, updatedFiles)];
                                    createAndStartDialogComponent.selectedFiles = [...selectedFiles];
                                }),
                                untilDestroyed(this),
                            )
                            .subscribe();
                    });
                }),
                takeUntil(this.createAndStartDialogClosed$$),
            )
            .subscribe();

        createAndStartDialogComponent.removeFiles
            .pipe(
                tap((removeFilesOptions: ItemsToBeRemovedOptions<FileModel>): void => {
                    const remainingFiles = selectedFiles.filter((file: FileModel): boolean => !removeFilesOptions.items.some(f => file.id === f.id));
                    selectedFiles = remainingFiles;
                    createAndStartDialogComponent.selectedFiles = [...selectedFiles];
                }),
                takeUntil(this.createAndStartDialogClosed$$),
            )
            .subscribe();
    }

    requestMlMeasurement(): void {
        const modalRef = this.modalService.open(ContactUsComponent);
        const modalComponent = modalRef.componentInstance as ContactUsComponent;
        modalComponent.title = this.translateService.instant('REQUEST_ML_MEASUREMENT_DIALOG.TITLE');
        modalComponent.description = this.translateService.instant('REQUEST_ML_MEASUREMENT_DIALOG.DESCRIPTION');
        modalComponent.messageLabel = this.translateService.instant('REQUEST_ML_MEASUREMENT_DIALOG.DESCRIPTION_OF_REQUEST');
        modalComponent.messagePlaceholder = this.translateService.instant('REQUEST_ML_MEASUREMENT_DIALOG.DESCRIPTION_OF_REQUEST_PLACEHOLDER');
        modalComponent.buttonText = this.translateService.instant('REQUEST_ML_MEASUREMENT_DIALOG.SEND_REQUEST');
        modalComponent.selectedSupportType = SupportTypes.RequestMlMeasurement;
        modalComponent.successModalTitle = this.translateService.instant('REQUEST_ML_MEASUREMENT_DIALOG.SUCCESS_MODAL.TITLE');
        modalComponent.successModalMessage = this.translateService.instant('REQUEST_ML_MEASUREMENT_DIALOG.SUCCESS_MODAL.MESSAGE');
    }

    // Helper function to proceed with the workflow
    proceedWithAriWorkflow$(
        modelConfig: ModelConfig,
        inputFiles: string[],
        applicationRunInformation: NameAndSelectedFilesContinueInformation,
    ): Observable<[{ workflowRunId: string }, NameAndSelectedFilesContinueInformation]> {
        return this.ariApplicationService
            .startAriApplicationByWorkflow$(modelConfig.workflowId, inputFiles, applicationRunInformation.resultName, true, modelConfig.mlModelId)
            .pipe(map(workflowRun => [{ workflowRunId: workflowRun.workflowRunId }, applicationRunInformation]));
    }

    proceedWithSegmentationWorkflow$(
        modelConfig: ModelConfig,
        settingsFileUrl: string,
        inputFiles: string[],
        applicationRunInformation: NameAndSelectedFilesContinueInformation,
    ): Observable<[{ workflowRunId: string }, NameAndSelectedFilesContinueInformation]> {
        return this.machineLearningService
            .startSegmentByWorkflowIdWithSettingsJson$(
                modelConfig.workflowId,
                modelConfig.modelUrl,
                modelConfig.configUrl,
                settingsFileUrl,
                inputFiles,
                applicationRunInformation.resultName,
                true,
                modelConfig.classes,
                modelConfig.mlModelId,
            )
            .pipe(map(workflowRun => [{ workflowRunId: workflowRun.workflowRunId }, applicationRunInformation]));
    }

    private handleStartWorkflowError$(err: unknown): Observable<never> {
        if (err instanceof HttpErrorResponse) {
            if (err.status === 403) {
                this.userNotificationService.showError(err.error);
            } else if (err.error?.description) {
                this.userNotificationService.showError(err.error.description as string);
            }
        } else {
            this.userNotificationService.showError('WORKFLOWS.ERROR_STARTING_WORKFLOW');
        }

        return EMPTY;
    }

    private openApplication(applicationId: string): void {
        this.router.navigate([`${APPLICATIONS_ROUTE}/${applicationId}`]);
    }
}
